// Stage manager for the Otto animation set — the two bits of coordination
// that can't live inside a single component.
//
// 1. EVENT SHOTS vs THE IDLE SCHEDULER (head-cock brief §8.1). Most of the
//    set is idle-scheduled by OttoRoamer. The head-cock is not: it belongs
//    to a request in flight. When the two collide the event shot wins — it
//    is tied to something the user actually did, the idle one is decoration
//    — and the idle timer restarts from scratch once the request finishes,
//    so he doesn't leap straight into a roam the moment a save lands.
//
// 2. TRANSIENT TRANSFORMS ON LIVE UI (lean/wobble brief §8.1). Shot 03
//    animates the real sidebar. A stuck transform on the primary navigation
//    is the worst realistic failure mode in the whole set, so nothing
//    touches live UI except through applyTransientClass, which force-clears
//    on a watchdog, on release, and whenever the tab goes away.
//
// The class-based form matters: the sidebar keyframes deliberately carry NO
// fill-mode, so even a class that somehow outlives its watchdog leaves the
// element back at identity once the animation finishes. An inline transform
// would have no such floor.

// ---- 1. event shots ----------------------------------------------------

const listeners = new Set();
let active = 0;

const emit = (phase) => {
  listeners.forEach((fn) => {
    try { fn(phase); } catch (e) { /* a bad listener must not strand the shot */ }
  });
};

export const isEventShotActive = () => active > 0;

// Returns the matching end() — call it exactly once; extra calls are no-ops
// so a component can end on both completion and unmount without double
// counting.
export function beginEventShot() {
  active += 1;
  if (active === 1) emit('start');
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    active = Math.max(0, active - 1);
    if (active === 0) emit('end');
  };
}

// cb('start') fires when the first event shot begins (pre-empt now),
// cb('end') when the last one finishes (safe to reschedule).
export function onEventShot(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// ---- 1b. ONE OTTO AT A TIME --------------------------------------------
//
// The roaming overlay draws its own Otto, but the bar Otto never went
// anywhere — so for the length of every roam there were visibly TWO of him on
// the page, which reads as a rendering fault rather than a joke.
//
// He is one owl. When a roam starts the bar Otto goes; when it ends he comes
// back. The roamer waits for him to go before its shot begins, so there is
// never a frame with two of him on the page.
//
// THE DEPARTURE IS NOT AN ANIMATION ANY MORE (Rob, 2026-08-14: "instead of him
// flying off ahead of each animation let's just have him disappear"). He is
// simply not drawn. That turned one constant into two, because they had been
// the same number by coincidence rather than because they measure the same
// thing:
//
//   HANDOFF_MS — how long the roamer waits before drawing its shot. Only
//     needs to outlast a render, not an animation. Keeping this at the old
//     620ms would leave half a second with NO Otto on screen at all, which
//     reads as a glitch far more than the fly-off ever did.
//
// WATCHDOGGED, because the failure here is a permanently empty briefing bar.
// If the roamer dies mid-shot — an exception, a route change, anything — the
// timer restores him anyway. A stuck roam costs an animation; a stuck
// departure costs Otto entirely.

const roamListeners = new Set();
let roamActive = false;
let roamWatchdog = null;

// One frame plus margin: long enough for the perched Otto's removal to paint,
// short enough that nobody perceives a gap.
export const HANDOFF_MS = 50;


export const isRoaming = () => roamActive;

const emitRoam = () => {
  roamListeners.forEach((fn) => {
    try { fn(roamActive); } catch (e) { /* a bad listener must not strand him */ }
  });
};

// Returns end(); safe to call more than once. `maxMs` is the backstop, not the
// expected length — the caller still ends it normally.
export function beginRoam(maxMs = 12000) {
  clearTimeout(roamWatchdog);
  roamActive = true;
  emitRoam();
  roamWatchdog = setTimeout(() => { roamActive = false; emitRoam(); }, maxMs + HANDOFF_MS + 1000);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    clearTimeout(roamWatchdog);
    roamActive = false;
    emitRoam();
  };
}

// cb(true) when the stage is handed to the roamer, cb(false) when it comes
// back. Fires immediately with the current value so a component mounting
// mid-roam doesn't render a second Otto for the rest of the shot.
export function onRoam(cb) {
  roamListeners.add(cb);
  try { cb(roamActive); } catch (e) { /* ignore */ }
  return () => roamListeners.delete(cb);
}

// ---- 2. transient classes on live UI -----------------------------------

const held = new Map(); // el -> timer

let watching = false;
const watchGlobals = () => {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  // A backgrounded tab freezes rAF but not necessarily our timers, and a
  // bfcache restore can resurrect a mid-animation DOM. Clear on both.
  const panic = () => releaseAllTransientClasses();
  document.addEventListener('visibilitychange', () => { if (document.hidden) panic(); });
  window.addEventListener('pagehide', panic);
};

export function releaseTransientClass(el) {
  const entry = held.get(el);
  if (!entry) return;
  clearTimeout(entry.timer);
  el.classList.remove(entry.cls);
  held.delete(el);
}

export function releaseAllTransientClasses() {
  Array.from(held.keys()).forEach(releaseTransientClass);
}

// Add `cls` to `el` for at most `ms`. Returns release(); the watchdog fires
// at ms + 250 as a backstop if the caller never does.
export function applyTransientClass(el, cls, ms) {
  if (!el || !el.classList) return () => {};
  watchGlobals();
  releaseTransientClass(el);              // never stack two shots on one element
  el.classList.add(cls);
  const timer = setTimeout(() => releaseTransientClass(el), ms + 250);
  held.set(el, { cls, timer });
  return () => releaseTransientClass(el);
}

// ---- shared DOM lookups ------------------------------------------------

// The internal nav rail. `aside` alone is ambiguous — BriefDetail's facts
// rail and SupplierPortal's column are both <aside> too, and shot 03
// TRANSFORMS whatever this returns, so it resolves the explicit marker
// first and only falls back to the old heuristic.
export const sidebarEl = () =>
  document.querySelector('[data-otto-sidebar]') || document.querySelector('aside');
